import { FormatHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$helpers';
import { Company } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$types';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import {
	AccountCardRow,
	Box,
	RadioButton,
	Spacer,
	Typography,
	useTheme,
} from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { avatarLogoBcp } from '@icons';
import { useSelectedAccount } from '@store';
import { Account, CompanyAccounts } from '@types';
import React from 'react';

interface AccountSelectorProps {
	companyAccounts: CompanyAccounts[];
}

export const AccountSelector: React.FC<AccountSelectorProps> = ({ companyAccounts }) => {
	const t = useTranslation();
	const { palette } = useTheme();
	const { setSelectedAccount, selectedAccount } = useSelectedAccount();

	const hasOnlyOneAccount = React.useCallback(() => {
		return companyAccounts.length === 1 && companyAccounts[0].accounts.length === 1;
	}, [companyAccounts]);

	const handleSelectAccount = (account: Account, companyName: string) => {
		setSelectedAccount(account, companyName);
	};

	React.useEffect(() => {
		!selectedAccount &&
			hasOnlyOneAccount() &&
			setSelectedAccount(companyAccounts[0].accounts[0], companyAccounts[0].companyName);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyAccounts]);

	return (
		<>
			<Typography appearance="headline-200-02">
				{hasOnlyOneAccount()
					? t('placeholders.accountSelector.title.contractLinkedAccount')
					: t('placeholders.accountSelector.title.selectAccountLinkedToContract')}
			</Typography>
			<Spacer vertical={40} />
			{companyAccounts.map(
				({ companyName, accounts }, index) =>
					!!accounts.length && (
						<div key={`${companyName}-${index}`}>
							<Typography appearance="subtitle-100-04">{companyName}</Typography>
							<Spacer vertical={16} />
							{accounts.map((account, accountIndex) => {
								const { name, number, accountableBalance, availableBalance, id } = account;
								return (
									<div key={`${number}-${accountIndex}`}>
										<Box
											styles={{ alignItems: 'center' }}
											onPress={() => handleSelectAccount(account, companyName)}
										>
											{!hasOnlyOneAccount() && (
												<>
													<RadioButton
														size={'small'}
														checked={selectedAccount?.account.id === id}
													/>
													<Spacer horizontal={16} />
												</>
											)}

											<AccountCardRow
												mode="detailed"
												account={{
													name: name,
													number: FormatHelper.formatAccountNumber(number, Company.ab),
													avatar: {
														source: avatarLogoBcp,
													},
												}}
												balance={{
													available: {
														amount: availableBalance,
														currency: 'EUR',
														label: t(
															'placeholders.accountSelector.details.label.availableBalance'
														),
													},
													accountable: {
														amount: accountableBalance,
														currency: 'EUR',
														label: t(
															'placeholders.accountSelector.details.label.accountableBalance'
														),
													},
												}}
												feedbackBar={{
													size: 'small',
													container: {
														backgroundColor: palette.primary.p1,
													},
												}}
											/>
										</Box>
										<Spacer vertical={accountIndex === accounts.length - 1 ? 40 : 16} />
									</div>
								);
							})}
						</div>
					)
			)}
		</>
	);
};
